"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.emptyEndorsement = void 0;
var react_1 = require("react");
// Data Layer
var QuotationCoverage_1 = require("../../models/QuotationCoverage");
var quotations_1 = require("../../functions/quotations");
/**
 * The initial value for the endorsement reference. We use a "mock" empty endorsement to
 * avoid dealing with nullable values.
 */
exports.emptyEndorsement = QuotationCoverage_1.makeEmptyQuotationCoverage();
/**
 * A helper method that creates a simplified coverage with the provided data.
 * @param code - The code to use as "coverageId"
 * @param value - The value to register as "insured value"
 */
function makeSimplifiedCoverage(code, value) {
    return __assign(__assign({}, QuotationCoverage_1.makeEmptyQuotationCoverage()), { coverageId: code, insuredValue: value, deductible: 0 });
}
/**
 * Coverage management inside `DynamicQuotationPage` was getting a bit out of hand, so I
 * decided to extract all the logic related to coverage transactions inside this hook. It
 * makes it so that all transactions are predictable, and all callback methods are called
 * when needed (this is important, as including this logic inside DynamicQuotationPage
 * directly was causing that component declaration to exceed the recommended max method
 * size, and was causing an infinite render loop error due to how the lifecycle was treated).
 *
 * @param selectedProduct - The selected product type
 * @param onCoveragesUpdated - Called when an update transaction is requested
 */
function useCoverageTracker(selectedProduct, onCoveragesUpdated) {
    var _a = react_1.useState([]), selectedCoverages = _a[0], updateSelectedCoverages = _a[1];
    var _b = react_1.useState(exports.emptyEndorsement), selectedEndorsement = _b[0], updateSelectedEndorsement = _b[1];
    var _c = react_1.useState(0), selectedDeductibleOption = _c[0], updateSelectedDeductibleOption = _c[1];
    /**
     * Perform this cleanup logic everytime the selected product updates. This ensures
     * that no coverages from the previous product leak to the new requests.
     */
    react_1.useEffect(function () {
        // Reset coverage and endorsement references
        updateSelectedCoverages([]);
        updateSelectedEndorsement(exports.emptyEndorsement);
        updateSelectedDeductibleOption(1);
    }, [selectedProduct, onCoveragesUpdated]);
    /**
     * Registers the provided coverage code and limit to the selected coverages reference.
     * Note that `simplifiedCoverageListWithout` will ensure no duplicate values are added.
     *
     * Notifies `onCoveragesUpdated` with new values
     *
     * @param code - The new coverage's code.
     * @param limit - The selected limit for the new coverage.
     */
    var addNewCoverage = react_1.useCallback(function (code, limit) {
        var newSelectedCoverages = quotations_1.coverageListWithout(selectedCoverages, code);
        newSelectedCoverages.push(makeSimplifiedCoverage(code, limit.maximum));
        var combinedLists = __spreadArrays([selectedEndorsement], newSelectedCoverages);
        onCoveragesUpdated(combinedLists, selectedDeductibleOption);
        updateSelectedCoverages(newSelectedCoverages);
    }, [
        selectedCoverages,
        selectedDeductibleOption,
        selectedEndorsement,
        onCoveragesUpdated,
    ]);
    /**
     * Registers the provided parameters as the selected endorsement option. Since there
     * can only be one endorsement, this logic is separate from regular coverages as all
     * transactions must overwrite the previous value.
     *
     * Notifies `onCoveragesUpdated` with new values
     *
     * @param code - The new endorsement's code
     * @param value - The new endorsement's value
     */
    var updateEndorsement = react_1.useCallback(function (code, value) {
        var newEndorsement = makeSimplifiedCoverage(code, value);
        var combinedLists = __spreadArrays([newEndorsement], selectedCoverages);
        onCoveragesUpdated(combinedLists, selectedDeductibleOption);
        updateSelectedEndorsement(newEndorsement);
    }, [selectedCoverages, selectedDeductibleOption, onCoveragesUpdated]);
    /**
     * Changes the current deductible option to the new value, and triggers the
     * `onCoveragesUpdated` callback.
     *
     * @param newOption - The new deductible option (from the slider)
     */
    var updateDeductibleOption = react_1.useCallback(function (newOption) {
        var combinedLists = __spreadArrays([selectedEndorsement], selectedCoverages);
        onCoveragesUpdated(combinedLists, newOption);
        updateSelectedDeductibleOption(newOption);
    }, [selectedEndorsement, selectedCoverages, onCoveragesUpdated]);
    /**
     * Overwrites all internal coverage values with the provided data. Note that this method
     * will not trigger `onCoverageUpdated` as it is considered a cleanup method and calling
     * it could result in an infinite render loop (see DynamicQuotationPage's implementation).
     *
     * @param simplifiedCoverages - The new list for the selectedCoverage reference.
     * @param endorsement - The new reference to use as the selectedEndorsement.
     */
    var overwriteAll = react_1.useCallback(function (simplifiedCoverages, endorsement) {
        updateSelectedCoverages(simplifiedCoverages);
        updateSelectedEndorsement(endorsement !== null && endorsement !== void 0 ? endorsement : exports.emptyEndorsement);
    }, []);
    return {
        selectedCoverages: selectedCoverages,
        selectedEndorsement: selectedEndorsement,
        selectedDeductibleOption: selectedDeductibleOption,
        addNewCoverage: addNewCoverage,
        updateEndorsement: updateEndorsement,
        updateDeductibleOption: updateDeductibleOption,
        overwriteAll: overwriteAll,
    };
}
exports.default = useCoverageTracker;
//# sourceMappingURL=useCoverageTracker.js.map